import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import useTaxFilingValidation from "../../hooks/useTaxFilingValidation";
import {
  PTF_SCHEMA,
  BTF_SCHEMA,
} from "../../utils/schemas/filings/taxSituationSchema";
import { buildSchemaMap } from "../../utils/collectors/GeneralSchemaCollector";
import { generateFormStateFromSchema } from "../../utils/generators/states/forms/filings/taxSituationFormState";
import VerticalFormWizard from "../wizards/VerticalFormWizard";
import AddressValidationModal from "../ui/popup/AddressValidationModal";
import useGoogleAddressValidation from "../../utils/forms/address/useGoogleAddressValidation";
import { mapSuggestedToRHF } from "../../utils/forms/address/addressValidationHelpers";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";

function TaxSituationForm({
  filing,
  onSubmit,
  onSubStepComplete,
  onPrev,
  service,
  schemaKey,
  onFormMethodsReady,
  onValidationChange,
}) {
  const [formState, setFormState] = useState(null);
  const wizardContainerRef = useRef(null);
  const SCHEMA_MAP = useMemo(
    () => buildSchemaMap({ PTF: PTF_SCHEMA, BTF: BTF_SCHEMA }),
    [],
  );
  const schemaSections = useMemo(
    () => (service && schemaKey ? SCHEMA_MAP[service]?.[schemaKey] || [] : []),
    [SCHEMA_MAP, service, schemaKey],
  );

  // Wizard submit callback: forward full form state (last sub-step)
  const handleWizardSubmit = (fullFormState) => {
    onSubmit(fullFormState);
  };

  useEffect(() => {
    setFormState(null);
    const generatedFormState = generateFormStateFromSchema({
      schemaSections,
      service,
      filing,
      schemaKey,
    });
    setFormState(generatedFormState);
  }, [schemaSections, filing, schemaKey, service]);

  // Initialize react-hook-form via dedicated hook
  const { formMethods } = useTaxFilingValidation({
    service,
    schemaKey,
    defaultValues: formState || {},
  });

  // Keep form default values in sync when formState changes
  useEffect(() => {
    if (formState) {
      formMethods.reset(formState);
    }
  }, [formState, formMethods]);

  // Sync incomeStates when taxYears changes (for PTF only)
  const taxYears = formMethods?.watch("taxYears");
  useEffect(() => {
    if (service === "PTF" && schemaKey === "Tax_Basics" && formMethods) {
      const currentIncomeStates = formMethods.getValues("incomeStates") || {};

      // Ensure incomeStates is an object (not an array from old data)
      const incomeStatesObj = Array.isArray(currentIncomeStates)
        ? {}
        : typeof currentIncomeStates === "object" &&
          currentIncomeStates !== null
        ? currentIncomeStates
        : {};

      // Get current tax years as strings for keys
      const currentYears = Array.isArray(taxYears) ? taxYears.map(String) : [];

      // Remove incomeStates for years that are no longer selected
      const updatedIncomeStates = { ...incomeStatesObj };
      Object.keys(updatedIncomeStates).forEach((year) => {
        if (!currentYears.includes(year)) {
          delete updatedIncomeStates[year];
        }
      });

      // Initialize empty arrays for new years that don't have incomeStates yet
      currentYears.forEach((year) => {
        if (!(year in updatedIncomeStates)) {
          updatedIncomeStates[year] = [];
        }
      });

      // Only update if there's a change
      const hasChanged =
        JSON.stringify(updatedIncomeStates) !== JSON.stringify(incomeStatesObj);
      if (hasChanged) {
        formMethods.setValue("incomeStates", updatedIncomeStates, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    }
  }, [service, schemaKey, formMethods, taxYears]);

  // Expose formMethods to parent once ready
  useEffect(() => {
    if (typeof onFormMethodsReady === "function") {
      onFormMethodsReady(formMethods);
    }
  }, [onFormMethodsReady, formMethods]);

  // Address validation modal (replicates CreateCPA/Personal flow)
  const {
    isOpen: addrModalOpen,
    close: closeAddrModal,
    userAddress: addrUser,
    suggestedAddress: addrSuggested,
    validateAndMaybeSuggest,
    acceptSuggestedAndApply,
  } = useGoogleAddressValidation();
  const [validatedAddress, setValidatedAddress] = useState(null);

  const toStateCode = useCallback(
    (s) => (s && s.toLowerCase ? s.toLowerCase() : s),
    [],
  );

  const normalizeAddressForCompare = useCallback(
    (a) => ({
      address: (a?.address || "").trim().toLowerCase(),
      suiteAddress: (a?.suiteAddress || "").trim().toLowerCase(),
      city: (a?.city || "").trim().toLowerCase(),
      stateAddress: toStateCode(a?.stateAddress),
      zipCode: (a?.zipCode || "").trim().toLowerCase(),
    }),
    [toStateCode],
  );

  const handleBeforeNext = async (stepIndex) => {
    if (!formMethods) return true;

    // Read current sub-fields from RHF; if not present, continue
    const v = formMethods.getValues();
    const user = {
      address: (v.address || "").trim(),
      suiteAddress: (v.suiteAddress || "").trim(),
      city: (v.city || "").trim(),
      stateAddress: (v.stateAddress || "").trim(),
      zipCode: (v.zipCode || "").trim(),
      country: v.country || "US",
      fromDate: "",
      toDate: "",
    };

    // If this step doesn't include address fields, or fields empty, let wizard proceed
    if (!user.address && !user.city && !user.stateAddress && !user.zipCode) {
      return true;
    }
    if (!user.address || !user.city || !user.stateAddress || !user.zipCode) {
      return true;
    }

    // Skip API call if already validated and unchanged
    if (validatedAddress) {
      const userNorm = normalizeAddressForCompare(user);
      const validatedNorm = normalizeAddressForCompare(validatedAddress);
      const same =
        userNorm.address === validatedNorm.address &&
        userNorm.suiteAddress === validatedNorm.suiteAddress &&
        userNorm.city === validatedNorm.city &&
        userNorm.stateAddress === validatedNorm.stateAddress &&
        userNorm.zipCode === validatedNorm.zipCode;
      if (same) return true;
    }

    const shouldContinue = await validateAndMaybeSuggest(user);
    if (shouldContinue) setValidatedAddress(user);
    return shouldContinue;
  };

  const applyAddressAndContinue = (addr) => {
    if (!formMethods) return;
    mapSuggestedToRHF(
      formMethods,
      {
        address: "address",
        suiteAddress: "suiteAddress",
        city: "city",
        stateAddress: "stateAddress",
        zipCode: "zipCode",
      },
      addr,
      { consolidatedField: "businessAddress" },
    );

    // Remember last validated address to avoid re-calling API
    setValidatedAddress(addr);

    // Continue to next step
    try {
      const container = wizardContainerRef.current;
      const nextBtn = container?.querySelector?.(".prevNext .btnAfter");
      if (nextBtn) nextBtn.click();
    } catch (e) {
      const evt = new CustomEvent("continueToNextStep");
      document.dispatchEvent(evt);
    }
  };

  // Check if form state is properly initialized
  const isFormReady =
    formState !== null &&
    Array.isArray(schemaSections) &&
    schemaSections.length > 0;

  if (!isFormReady) {
    return <LoadingPlaceholder />;
  }

  return (
    <div className="columns" ref={wizardContainerRef}>
      <AddressValidationModal
        open={addrModalOpen}
        onClose={closeAddrModal}
        userAddress={addrUser}
        suggestedAddress={addrSuggested}
        onAccept={(choice) =>
          acceptSuggestedAndApply(() => applyAddressAndContinue(choice))
        }
      />

      <VerticalFormWizard
        schemaSections={schemaSections}
        initialFormState={formState}
        formMethods={formMethods}
        onValidationChange={onValidationChange}
        onSubmit={handleWizardSubmit}
        onSubStepComplete={onSubStepComplete}
        onPrevFromFirstSubStep={onPrev}
        onBeforeNext={handleBeforeNext}
        nextButtonText="Next"
      />
    </div>
  );
}

export default TaxSituationForm;
